<div class="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col gap-6">
  <!-- HERO PHOTO GALLERY (Leboncoin Mosaic Style) -->
  @if (showPhotos()) {
    <etxe-property-photos-display variant="mosaic" [property]="property()" [isOwner]="isOwnerView()" />
  }

  <!-- MAIN 2-COLUMN LAYOUT -->
  <div class="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
    <!-- ========================================== -->
    <!-- LEFT COLUMN: MAIN LISTING CONTENT (8 or 12 COLS) -->
    <!-- ========================================== -->
    <div [class]="hideSidebar() ? 'lg:col-span-12 flex flex-col gap-8' : 'lg:col-span-8 flex flex-col gap-8'">
      <!-- 1. TITLE, PRICE & QUICK METRICS HEADER -->
      <etxe-property-view-headline [property]="property()" />

      <hr class="border-gray-5 m-0" />

      <!-- 2. CRITÈRES ESSENTIELS, ACCESSIBILITÉ & BÂTIMENT -->
      <etxe-property-view-key-criteria [property]="property()" />

      <!-- 3. CONDITIONS & RÈGLES DE MANDAT -->
      @if (showMandateConditions() && property().mandate) {
        <hr class="border-gray-5 m-0" />

        <div class="flex flex-col gap-4">
          <div class="flex items-center justify-between">
            <h2 class="text-xl font-bold text-gray-12 m-0 flex items-center gap-2.5">
              <mat-icon class="text-indigo-9">handshake</mat-icon>
              <span>Conditions & Règles de mandat</span>
            </h2>
          </div>

          <etxe-property-mandate-display
            [mandate]="property().mandate"
            [sellingPriceCents]="property().financials?.sellingPriceCents"
          />
        </div>
      }

      <!-- 4. CONSTRUCTION & TECHNIQUE -->
      @if (property().construction) {
        <hr class="border-gray-5 m-0" />

        <div class="flex flex-col gap-6">
          <h2 class="text-xl font-bold text-gray-12 m-0 flex items-center gap-2.5">
            <mat-icon class="text-indigo-9">foundation</mat-icon>
            <span>Construction & Technique</span>
          </h2>

          <etxe-property-construction-display
            [propertyDefinition]="property().definition"
            [construction]="property().construction"
          />
        </div>
      }

      <!-- 5. CARACTÉRISTIQUES DÉTAILLÉES (PIÈCES, ESPACES & ÉQUIPEMENTS) -->
      @if (property().characteristics) {
        <hr class="border-gray-5 m-0" />

        <div class="flex flex-col gap-6">
          <h2 class="text-xl font-bold text-gray-12 m-0 flex items-center gap-2.5">
            <mat-icon class="text-indigo-9">list</mat-icon>
            <span>Caractéristiques détaillées</span>
          </h2>

          <etxe-property-characteristics-display [characteristics]="property().characteristics" />
        </div>
      }

      <!-- 5. DIAGNOSTICS ÉNERGÉTIQUES (DPE / GES) -->
      @if (property().dpe) {
        <hr class="border-gray-5 m-0" />

        <mat-accordion class="w-full !shadow-none !border-none">
          <mat-expansion-panel class="!bg-transparent !shadow-none !border-none !border-0 !outline-none">
            <mat-expansion-panel-header class="!px-0 hover:!bg-transparent !border-none !outline-none">
              <mat-panel-title class="!m-0">
                <div class="flex flex-col gap-0.5">
                  <div class="flex items-center gap-2.5 text-gray-12 font-bold text-xl">
                    <mat-icon class="text-indigo-9">speed</mat-icon>
                    <span>Diagnostic de performance énergétique (DPE)</span>
                  </div>
                  <span class="text-xs text-gray-10 font-normal ml-8 hidden sm:block">
                    Classes énergie et climat, consommation kWh/m², isolation et système de chauffage
                  </span>
                </div>
              </mat-panel-title>
            </mat-expansion-panel-header>

            <div class="pt-6">
              <etxe-property-dpe-display [property]="property()" />
            </div>
          </mat-expansion-panel>
        </mat-accordion>
      }

      <!-- 6. DONNÉES GÉOSPATIALES -->
      <hr class="border-gray-5 m-0" />

      <mat-accordion class="w-full !shadow-none !border-none">
        <mat-expansion-panel class="!bg-transparent !shadow-none !border-none !border-0 !outline-none">
          <mat-expansion-panel-header class="!px-0 hover:!bg-transparent !border-none !outline-none">
            <mat-panel-title class="!m-0">
              <div class="flex flex-col gap-0.5">
                <div class="flex items-center gap-2.5 text-gray-12 font-bold text-xl">
                  <mat-icon class="text-indigo-9">travel_explore</mat-icon>
                  <span>Données géospatiales</span>
                </div>
                <span class="text-xs text-gray-10 font-normal ml-8 hidden sm:block">
                  Cadastre, risques naturels, revenus INSEE, acoustique et urbanisme
                </span>
              </div>
            </mat-panel-title>
          </mat-expansion-panel-header>

          <div class="pt-6">
            <etxe-property-geospatial-display
              [property]="property()"
              [fetchData]="true"
              [showHero]="false"
              [isOwner]="isOwnerView()"
              (propertyRefreshed)="propertyUpdated.emit($event)"
            />
          </div>
        </mat-expansion-panel>
      </mat-accordion>

      <!-- 7. LOCALISATION & POINTS D'INTÉRÊT -->
      @if (property().location?.coordinates?.latitude && property().location?.coordinates?.longitude) {
        <hr class="border-gray-5 m-0" />

        <div class="flex flex-col gap-6">
          <div class="flex items-center justify-between">
            <h2 class="text-xl font-bold text-gray-12 m-0 flex items-center gap-2.5">
              <mat-icon class="text-indigo-9">map</mat-icon>
              <span>Localisation & Quartier</span>
            </h2>
          </div>

          <div class="flex items-center justify-between flex-wrap gap-2">
            <div class="flex flex-col">
              <div class="font-semibold text-gray-12 flex items-center gap-1.5">
                <mat-icon class="text-indigo-11 text-base! w-4! h-4!">location_on</mat-icon>
                @if (isOwnerView() && property().location?.address?.street) {
                  <span>{{ property().location?.address?.street }}</span>
                } @else {
                  <span
                    >{{ property().location?.address?.city }} ({{ property().location?.address?.zipCode }}),
                    @if (property().location?.address?.region) {
                      <span class="text-gray-10">{{ property().location?.address?.region }}</span>
                    }
                  </span>
                }
              </div>
              @if (isOwnerView() && property().location?.address?.street) {
                <div class="text-gray-11 font-medium ml-5.5">
                  {{ property().location?.address?.zipCode }} {{ property().location?.address?.city }}
                  @if (property().location?.address?.region) {
                    <span class="text-gray-10">({{ property().location?.address?.region }})</span>
                  }
                </div>
              }
            </div>
            @if (isOwnerView() && property().location?.geolocationType; as geoType) {
              <span
                class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-gray-3 text-gray-11 border border-gray-6"
              >
                <mat-icon class="text-sm! w-4! h-4! text-indigo-10">radar</mat-icon>
                <span>Précision : {{ geoType | etxeEnumDisplay: 'geolocationType' }}</span>
              </span>
            }
          </div>

          @if (!isOwnerView()) {
            <div
              class="flex items-start gap-3 p-3.5 rounded-2xl bg-indigo-a2/70 border border-indigo-a4 text-gray-12 text-xs leading-relaxed"
            >
              <div
                class="w-8 h-8 rounded-xl bg-indigo-a3 text-indigo-11 flex items-center justify-center shrink-0 mt-0.5"
              >
                <mat-icon class="text-base! w-4! h-4!">lock</mat-icon>
              </div>
              <div class="flex flex-col gap-1">
                <span class="font-bold text-indigo-12">
                  L'adresse exacte du bien sera dévoilée 24h avant le début des visites.
                </span>
                @if (property().location?.geolocationType; as geoType) {
                  <span class="text-gray-11">
                    Le propriétaire a décidé d'afficher une précision du bien par
                    <span class="font-semibold text-gray-12"
                      >« {{ geoType | etxeEnumDisplay: 'geolocationType' }} »</span
                    >.
                  </span>
                }
              </div>
            </div>
          }

          <etxe-property-location-display
            [location]="property().location"
            [hideAddress]="true"
            [isOwner]="isOwnerView()"
            [showPoiToolbar]="true"
            [showProximityList]="true"
          />
        </div>
      }

      <!-- 8. CADASTRE ET PARCELLE -->
      @if (
        isOwnerView() &&
        ((property().location?.coordinates?.latitude && property().location?.coordinates?.longitude) ||
          property().geospatial?.cadastral)
      ) {
        <hr class="border-gray-5 m-0" />

        <mat-accordion class="w-full !shadow-none !border-none">
          <mat-expansion-panel class="!bg-transparent !shadow-none !border-none !border-0 !outline-none">
            <mat-expansion-panel-header class="!px-0 hover:!bg-transparent !border-none !outline-none">
              <mat-panel-title class="!m-0">
                <div class="flex items-center justify-between w-full">
                  <div class="flex flex-col gap-0.5">
                    <div class="flex items-center gap-2.5 text-gray-12 font-bold text-xl">
                      <mat-icon class="text-indigo-9">grid_4x4</mat-icon>
                      <span>Cadastre et parcelle</span>
                    </div>
                    <span class="text-xs text-gray-10 font-normal ml-8 hidden sm:block">
                      Plan cadastral officiel, contenance foncière et emprise du bâti
                    </span>
                  </div>

                  @if (property().geospatial?.cadastral?.totalSurface; as surface) {
                    <span
                      class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-jade-a2 text-jade-11 border border-jade-a4 shadow-2xs mr-2"
                    >
                      <mat-icon class="text-sm! w-4! h-4! text-jade-10">verified</mat-icon>
                      <span>{{ surface | number: '1.0-0' : 'fr-FR' }} m²</span>
                    </span>
                  }
                </div>
              </mat-panel-title>
            </mat-expansion-panel-header>

            <div class="pt-6">
              <etxe-property-cadastral-card
                [cadastral]="property().geospatial?.cadastral"
                [addressLabel]="property().location?.address?.label || property().location?.address?.street"
                [cityCode]="property().location?.address?.cityCode"
                [coordinates]="property().location?.coordinates"
                [isOwner]="isOwnerView()"
                [propertyId]="property()._id"
                variant="flat"
              />
            </div>
          </mat-expansion-panel>
        </mat-accordion>
      }

      <!-- SELLER INFOS -->
      @if (property().owner; as owner) {
        <etxe-property-seller-info class="md:col-span-2" [owner]="owner" [isOwner]="isOwnerView()" />
      }
    </div>

    <!-- ============================================= -->
    <!-- RIGHT COLUMN: SELLER & ACTIONS SIDEBAR (4 COLS) -->
    <!-- ============================================= -->
    @if (!hideSidebar()) {
      @if (showDefaultSidebar()) {
        <etxe-property-view-sidebar
          [property]="property()"
          [isOwner]="isCurrentUserOwner()"
          [isCoManager]="isCurrentUserCoManager()"
          [missingItemsCount]="missingItemsCount()"
          [demoMode]="demoMode()"
          (shareClicked)="shareListing()"
          (previewAgentClicked)="previewAgentClicked.emit()"
          (propertyUpdated)="propertyUpdated.emit($event)"
        />
      } @else {
        <div class="col-span-1 lg:col-span-4 flex flex-col gap-6 lg:sticky lg:top-6">
          <ng-content select="[view-sidebar]" />
        </div>
      }
    }
  </div>
</div>
